import { ChevronDown, ExternalLink, Loader2, MessageSquare } from 'lucide-react';
import React, { useCallback, useState } from 'react';

import { useIntakePage } from '@/hooks/useIntakePage';
import { cn } from '@/lib/utils';
import { useGetFirmQuery } from '@/services/api/firmsService';
import { IntakeStatus } from '@/services/types/intake-types';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../ui/accordion';
import { Button } from '../../ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../ui/collapsible';
import { AutoChaseCard } from '../components/AutoChaseCard';

interface ScreeningRightPanelProps {
  firmId: string | undefined;
  incidentDescription: string;
  onIncidentDescriptionChange: (value: string) => void;
  intakeId?: string;
  intakeStatus?: string;
  clientName?: string;
  clientPhone?: string;
  intakerName?: string;
  /** Hide intake notes (V2 has its own notes in ReviewRightPanel) */
  hideIntakeNotes?: boolean;
}

export const ScreeningRightPanel: React.FC<ScreeningRightPanelProps> = ({
  firmId,
  incidentDescription,
  onIncidentDescriptionChange,
  intakeId,
  intakeStatus,
  clientName,
  clientPhone,
  intakerName,
  hideIntakeNotes = false,
}) => {
  const { data: firm, isLoading } = useGetFirmQuery(firmId ?? '', {
    skip: !firmId,
  });

  const sections = firm?.intake_preferences?.sections || [];
  const [guidelinesOpen, setGuidelinesOpen] = useState(true);

  return (
    <div className="flex h-full bg-zinc-100">
      {/* Main Content */}
      <div className="flex-1 flex flex-col p-3 gap-3 overflow-hidden">
        {/* Auto Chase Card - only shown for chase status intakes */}
        {intakeId && firmId && intakeStatus === IntakeStatus.CHASE && (
          <AutoChaseCard
            intakeId={intakeId}
            firmId={firmId}
            clientName={clientName}
            clientPhone={clientPhone}
            intakerName={intakerName}
          />
        )}

        {/* Incident Description Card */}
        <div className={cn(
          'bg-white rounded-[20px] border border-zinc-200/80 flex flex-col overflow-hidden',
          guidelinesOpen ? 'h-2/5' : 'flex-1',
        )}>
          <div className="px-4 pt-4 pb-2">
            <h3 className="text-sm font-bold text-zinc-900 tracking-tight">
              Notes
            </h3>
          </div>
          <div className="flex-1 px-4 pb-4 min-h-0">
            <textarea
              placeholder="Type detailed notes here..."
              value={incidentDescription}
              onChange={(e) => onIncidentDescriptionChange(e.target.value)}
              className="w-full h-full resize-none text-sm bg-zinc-50 rounded-xl border-0 p-3 focus:outline-none focus:ring-2 focus:ring-zinc-900 placeholder:text-zinc-300 font-medium"
            />
          </div>
        </div>

        {/* Intake Notes Timeline — only for V1 (V2 has its own in ReviewRightPanel) */}
        {intakeId && !hideIntakeNotes && (
          <IntakeNotesCard intakeId={intakeId} />
        )}

        {/* Firm Guidelines Card - Collapsible */}
        <Collapsible
          open={guidelinesOpen}
          onOpenChange={setGuidelinesOpen}
          className={cn(
            'flex flex-col min-h-0 transition-all duration-200',
            guidelinesOpen ? 'flex-1' : 'flex-none',
          )}
        >
          <div className={cn(
            'bg-white rounded-[20px] border border-zinc-200/80 flex flex-col overflow-hidden',
            guidelinesOpen ? 'flex-1' : '',
          )}>
            <div className={cn(
              'px-4 flex items-center justify-between shrink-0',
              guidelinesOpen ? 'pt-4 pb-2' : 'py-3.5',
            )}>
              <CollapsibleTrigger className="flex items-center gap-1.5 group cursor-pointer">
                <div>
                  <h3 className="text-sm font-bold text-zinc-900 tracking-tight text-left">
                    {firm?.name || 'Firm'} Guidelines
                  </h3>
                  {guidelinesOpen && sections.length > 0 && (
                    <p className="text-[10px] text-zinc-500 font-medium text-left">
                      {sections.length}{' '}
                      {sections.length === 1 ? 'section' : 'sections'}
                    </p>
                  )}
                </div>
                <ChevronDown className={cn(
                  'h-4 w-4 text-zinc-500 transition-transform duration-200',
                  guidelinesOpen && 'rotate-180',
                )} />
              </CollapsibleTrigger>
              {firm?.lead_system_link && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs gap-1.5 rounded-xl font-bold border-zinc-200 hover:bg-zinc-50"
                  onClick={() =>
                    window.open(firm?.lead_system_link ?? '', '_blank')
                  }
                >
                  <ExternalLink className="h-3 w-3" />
                  CMS
                </Button>
              )}
            </div>

            <CollapsibleContent className="flex-1 overflow-y-auto px-4 pb-4">
              {isLoading && (
                <div className="flex items-center justify-center py-8">
                  <div className="w-6 h-6 border-2 border-zinc-200 border-t-zinc-900 rounded-full animate-spin" />
                </div>
              )}

              {!isLoading && !firmId && (
                <div className="text-center py-8">
                  <p className="text-xs text-zinc-500 font-medium">
                    Select a firm to view guidelines
                  </p>
                </div>
              )}

              {!isLoading && firmId && sections.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-xs text-zinc-500 font-medium">
                    No guidelines configured
                  </p>
                </div>
              )}

              {!isLoading && sections.length > 0 && (
                <Accordion type="multiple" className="w-full space-y-2">
                  {sections.map(
                    (
                      section: { title: string; content: string },
                      index: number,
                    ) => (
                      <AccordionItem
                        key={index}
                        value={`section-${index}`}
                        className={cn(
                          'border border-zinc-100 rounded-xl overflow-hidden bg-zinc-50/50',
                          'data-[state=open]:bg-zinc-50 data-[state=open]:border-zinc-200',
                        )}
                      >
                        <AccordionTrigger className="text-left text-xs font-bold hover:no-underline px-3 py-2.5 hover:bg-zinc-100/50">
                          {section.title}
                        </AccordionTrigger>
                        <AccordionContent>
                          <p className="text-xs text-zinc-600 whitespace-pre-line leading-relaxed px-3 pb-3">
                            {section.content}
                          </p>
                        </AccordionContent>
                      </AccordionItem>
                    ),
                  )}
                </Accordion>
              )}
            </CollapsibleContent>
          </div>
        </Collapsible>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Intake Notes Card — timeline of notes with add input
// ---------------------------------------------------------------------------

const IntakeNotesCard: React.FC<{ intakeId: string }> = ({ intakeId }) => {
  const {
    intake: { form, addGeneralNote },
  } = useIntakePage(intakeId, false);

  const [draft, setDraft] = useState('');
  const [isSending, setIsSending] = useState(false);

  const notes = [...(form.generalNotesLog || [])].reverse();

  const handleSubmit = useCallback(async () => {
    const text = draft.trim();
    if (!text || isSending) return;
    setIsSending(true);
    try {
      await addGeneralNote(text);
      setDraft('');
    } finally {
      setIsSending(false);
    }
  }, [draft, isSending, addGeneralNote]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  }, [handleSubmit]);

  const formatTime = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  return (
    <div className="bg-white rounded-[20px] border border-zinc-200/80 flex flex-col overflow-hidden">
      <div className="px-4 pt-4 pb-2 flex items-center gap-2">
        <MessageSquare className="size-3.5 text-zinc-400" />
        <h3 className="text-sm font-bold text-zinc-900 tracking-tight">Intake Notes</h3>
        {notes.length > 0 && (
          <span className="text-[10px] font-bold text-zinc-400 ml-auto">{notes.length}</span>
        )}
      </div>

      {/* Add note input — at top */}
      <div className="px-4 pb-2">
        <div className="flex gap-1.5">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={1}
            className="flex-1 px-2.5 py-1.5 text-[11px] font-medium text-zinc-900 bg-zinc-50 border border-zinc-200 rounded-lg resize-none focus:outline-none focus:ring-1 focus:ring-zinc-900 placeholder:text-zinc-400"
            placeholder="Add a note..."
          />
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!draft.trim() || isSending}
            className={cn(
              'px-2.5 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wide transition-all',
              draft.trim() ? 'bg-zinc-900 text-white hover:bg-zinc-800' : 'bg-zinc-100 text-zinc-400 cursor-not-allowed',
            )}
          >
            {isSending ? <Loader2 className="size-3 animate-spin" /> : 'Post'}
          </button>
        </div>
      </div>

      {/* Notes list — latest first */}
      {notes.length > 0 && (
        <div className="max-h-[180px] overflow-y-auto px-4 pb-3 space-y-2">
          {notes.map((note: { at: string; text: string; authorName?: string }, idx: number) => (
            <div key={idx}>
              <div className="flex items-baseline gap-1.5">
                <span className="text-[10px] font-bold text-zinc-900 shrink-0">{note.authorName || 'System'}</span>
                <span className="text-[9px] font-medium text-zinc-400">{formatTime(note.at)}</span>
              </div>
              <p className="text-[11px] font-medium text-zinc-600 mt-0.5 leading-relaxed">{note.text}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
